import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SearchBox from "@/components/feature/SearchBox";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import { allCharacters, getHotWorks, getWorkById, searchWorks } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 8;

export default function Search() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") || "").trim();
  const [page, setPage] = useState(1);

  const works = useMemo(() => searchWorks(keyword), [keyword]);
  const characterMatches = useMemo(() => {
    if (!keyword) return [];
    const key = keyword.toLowerCase();
    return allCharacters.filter((character) =>
      [character.name, character.actor, character.identity, ...character.tags].join(" ").toLowerCase().includes(key)
    );
  }, [keyword]);

  const total = works.length + characterMatches.length;
  const totalPages = Math.max(1, Math.ceil(works.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = works.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hotWorks = getHotWorks(4);

  useDocumentTitle(
    keyword ? `“${keyword}”的搜索结果 - 亲吻姐姐` : "搜索影视作品与角色 - 亲吻姐姐",
    keyword
      ? `亲吻姐姐搜索结果页，展示与“${keyword}”相关的影视作品与角色分析内容。`
      : "在亲吻姐姐搜索影视作品名称、导演、演员与角色名，快速找到剧情介绍与角色分析。"
  );

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12 lg:px-8">
          <Breadcrumb items={[{ label: "搜索结果" }]} />
          <h1 className="mt-5 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            搜索结果
            {keyword && (
              <span className="ml-2 text-primary-700">
                “<span className="bg-accent-100 px-1.5 text-accent-800">{keyword}</span>”
              </span>
            )}
          </h1>
          <div className="mt-6 max-w-2xl">
            <SearchBox size="lg" defaultValue={keyword} />
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10 lg:px-8">
        {!keyword ? (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">输入关键词开始搜索</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              支持影视作品名称、导演、演员以及角色名的模糊查询，例如“锦岁长安”“沈砚”“悬疑”。
            </p>
          </div>
        ) : total === 0 ? (
          <div>
            <div className="flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">未找到“{keyword}”相关内容</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者看看下面的热门影视推荐。
              </p>
            </div>

            <h3 className="mt-10 font-heading text-xl font-extrabold text-foreground-950">热门影视推荐</h3>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {hotWorks.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground-600">
              共找到 <strong className="font-heading text-primary-700">{total}</strong> 条与“{keyword}”相关的内容
              <span className="text-foreground-400">（影视作品 {works.length} · 角色 {characterMatches.length}）</span>
            </p>

            {pageItems.length > 0 && (
              <>
                <h2 className="mt-6 font-heading text-lg font-bold text-foreground-950">影视作品</h2>
                <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {pageItems.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
                <Pagination current={safePage} total={totalPages} onChange={setPage} />
              </>
            )}

            {characterMatches.length > 0 && (
              <>
                <h2 className="mt-12 font-heading text-lg font-bold text-foreground-950">相关角色</h2>
                <div className="mt-4 flex flex-col gap-4">
                  {characterMatches.map((character) => {
                    const work = getWorkById(character.workId);
                    return (
                      <Link
                        key={character.id}
                        to={`/character/${character.id}`}
                        className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100"
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-background-200">
                          <img
                            src={character.image}
                            alt={`${character.name} 角色形象`}
                            title={`${character.name} 角色分析`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                            {character.name}
                          </p>
                          <p className="truncate text-xs text-foreground-500">
                            {character.identity} · 来自《{work ? work.title : "未知作品"}》
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line text-xl text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-600"></i>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </SiteLayout>
  );
}